import { useCallback, useEffect, useState } from "react";
import CookieBannerBar from "./cookies/CookieBannerBar";
import CookiePreferencesModal from "./cookies/CookiePreferencesModal";
import { ALL_ACCEPTED, ALL_REFUSED, DEFAULT_STATE, readCookie, writeCookie } from "./cookies/cookieStore";

// Phase 6 · CMP cookies — bannière + gestion granulaire conforme RGPD.
// Catégories : necessary (non refusable), functional, analytics, marketing.

/**
 * Hook public — permet aux composants de savoir si une catégorie est autorisée.
 * Utilisation : const { analytics } = useCookieConsent();  // bool
 */
export function useCookieConsent() {
  const [state, setState] = useState(() => readCookie() || DEFAULT_STATE);
  useEffect(() => {
    const onStorage = () => setState(readCookie() || DEFAULT_STATE);
    window.addEventListener("vp:cookie-consent-updated", onStorage);
    return () => window.removeEventListener("vp:cookie-consent-updated", onStorage);
  }, []);
  return state;
}

export default function CookieBanner() {
  const [state, setState] = useState(() => readCookie());
  const [showModal, setShowModal] = useState(false);
  const [granular, setGranular] = useState(state || DEFAULT_STATE);

  useEffect(() => { setGranular(state || DEFAULT_STATE); }, [state]);

  const save = useCallback((next) => {
    writeCookie(next);
    setState(next);
    setShowModal(false);
    window.dispatchEvent(new CustomEvent("vp:cookie-consent-updated"));
  }, []);

  const acceptAll = () => save(ALL_ACCEPTED);
  const refuseAll = () => save(ALL_REFUSED);
  const saveGranular = () => save({ ...granular, necessary: true });

  return (
    <>
      {/* Bannière visible uniquement tant qu'aucun choix n'a été fait */}
      {!state && (
        <CookieBannerBar
          onRefuseAll={refuseAll}
          onCustomize={() => setShowModal(true)}
          onAcceptAll={acceptAll}
        />
      )}

      {showModal && (
        <CookiePreferencesModal
          granular={granular}
          setGranular={setGranular}
          onClose={() => setShowModal(false)}
          onRefuseAll={refuseAll}
          onSave={saveGranular}
        />
      )}
    </>
  );
}

/**
 * Bouton d'accès depuis le footer / une page — ouvre directement le modal de gestion,
 * même si un choix a déjà été fait.
 */
export function CookieSettingsLink({ className = "" }) {
  return (
    <button
      data-testid="cookie-settings-open"
      onClick={() => window.dispatchEvent(new CustomEvent("vp:cookie-open-modal"))}
      className={className}
    >
      Gérer mes cookies
    </button>
  );
}
